import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import type {
  CreateSourceFileMetadataInput,
  CreateSourceSetInput,
  CreateSourceUrlMetadataInput,
  UpdateSourceSetSettingsInput,
  SourceFile,
  SourceFileType,
  SourceSet,
  SourceSetIntendedOutput,
  SourceSetStatus,
  SourceSetValidationResult
} from '../types/sourceSet';

export const SOURCE_SET_LIMITS = {
  MAX_FILES_PER_SOURCE_SET: 5,
  MAX_URLS_PER_SOURCE_SET: 3,
  MAX_SINGLE_FILE_BYTES: 25 * 1024 * 1024,
  MAX_TOTAL_BYTES: 50 * 1024 * 1024,
  SUPPORTED_FILE_TYPES: ['pdf', 'docx', 'pptx'] as SourceFileType[]
};

export function normaliseSourceSetTitle(title: string): string {
  return String(title || '').replace(/\s+/g, ' ').trim();
}

export function getSourceFileType(fileName: string, mimeType?: string): SourceFileType {
  const lowerName = String(fileName || '').toLowerCase();
  const lowerMime = String(mimeType || '').toLowerCase();

  if (lowerName.endsWith('.pdf') || lowerMime.includes('pdf')) {
    return 'pdf';
  }

  if (
    lowerName.endsWith('.docx') ||
    lowerMime.includes('wordprocessingml') ||
    lowerMime.includes('msword')
  ) {
    return 'docx';
  }

  if (
    lowerName.endsWith('.pptx') ||
    lowerMime.includes('presentationml') ||
    lowerMime.includes('powerpoint')
  ) {
    return 'pptx';
  }

  return 'unknown';
}

export function isSupportedSourceFile(fileName: string, mimeType?: string): boolean {
  const fileType = getSourceFileType(fileName, mimeType);
  return SOURCE_SET_LIMITS.SUPPORTED_FILE_TYPES.includes(fileType);
}

export function makeSafeSourceFileName(fileName: string): string {
  const safeName = String(fileName || 'source-file')
    .replace(/[^\w.\-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();

  return safeName || 'source-file';
}

export function buildSourceSetStoragePath(args: {
  userId: string;
  sourceSetId: string;
  fileId: string;
  fileName: string;
  organisationId?: string | null;
}): string {
  const safeFileName = makeSafeSourceFileName(args.fileName);

  if (args.organisationId) {
    return `organisations/${args.organisationId}/sourceSets/${args.sourceSetId}/${args.fileId}-${safeFileName}`;
  }

  return `users/${args.userId}/sourceSets/${args.sourceSetId}/${args.fileId}-${safeFileName}`;
}

export function normaliseSourceUrl(url: string): string {
  return String(url || '').trim();
}

export function getUrlDisplayName(url: string): string {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.hostname.replace(/^www\./, '') || url;
  } catch {
    return url;
  }
}

export function validateSourceSetUrl(
  url: string,
  existingSources: SourceFile[] = []
): SourceSetValidationResult {
  const errors: string[] = [];
  const normalisedUrl = normaliseSourceUrl(url);

  if (!normalisedUrl) {
    errors.push('Please enter a webpage URL.');
    return { valid: false, errors };
  }

  try {
    const parsedUrl = new URL(normalisedUrl);
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      errors.push('Only http and https URLs are supported.');
    }
  } catch {
    errors.push('Please enter a valid webpage URL.');
  }

  if (existingSources.length >= SOURCE_SET_LIMITS.MAX_FILES_PER_SOURCE_SET) {
    errors.push(`You can add up to ${SOURCE_SET_LIMITS.MAX_FILES_PER_SOURCE_SET} sources in one Source Set.`);
  }

  const existingUrls = existingSources.filter((source) => source.sourceKind === 'url' || source.fileType === 'url');

  if (existingUrls.length >= SOURCE_SET_LIMITS.MAX_URLS_PER_SOURCE_SET) {
    errors.push(`You can add up to ${SOURCE_SET_LIMITS.MAX_URLS_PER_SOURCE_SET} URLs in one Source Set.`);
  }

  const duplicateUrl = existingUrls.some((source) => normaliseSourceUrl(source.url || '') === normalisedUrl);
  if (duplicateUrl) {
    errors.push('This URL is already in the Source Set.');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

export function validateSourceSetFiles(
  files: File[],
  existingFileCount: number = 0,
  existingTotalBytes: number = 0
): SourceSetValidationResult {
  const errors: string[] = [];

  if (!files || files.length === 0) {
    errors.push('Please select at least one file.');
    return { valid: false, errors };
  }

  const totalFileCount = existingFileCount + files.length;

  if (totalFileCount > SOURCE_SET_LIMITS.MAX_FILES_PER_SOURCE_SET) {
    errors.push(`You can upload up to ${SOURCE_SET_LIMITS.MAX_FILES_PER_SOURCE_SET} files in one Source Set.`);
  }

  const newFilesTotalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const combinedTotalBytes = existingTotalBytes + newFilesTotalBytes;

  if (combinedTotalBytes > SOURCE_SET_LIMITS.MAX_TOTAL_BYTES) {
    errors.push('The total upload size for one Source Set cannot exceed 50MB.');
  }

  for (const file of files) {
    if (file.size > SOURCE_SET_LIMITS.MAX_SINGLE_FILE_BYTES) {
      errors.push(`"${file.name}" is too large. Each file must be 25MB or smaller.`);
    }

    if (!isSupportedSourceFile(file.name, file.type)) {
      errors.push(`"${file.name}" is not supported. Source Sets currently support PDF, DOCX, and PPTX files.`);
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

export async function createSourceSet(input: CreateSourceSetInput): Promise<string> {
  const title = normaliseSourceSetTitle(input.title);

  if (!input.ownerId) {
    throw new Error('A logged-in user is required to create a Source Set.');
  }

  if (!title) {
    throw new Error('Source Set title is required.');
  }

  const sourceSetRef = doc(collection(db, 'sourceSets'));
  const intendedOutput: SourceSetIntendedOutput = input.intendedOutput || 'undecided';

  const sourceSet: SourceSet = {
    sourceSetId: sourceSetRef.id,
    ownerId: input.ownerId,
    organisationId: input.organisationId || null,
    title,
    description: input.description || '',
    status: 'draft',
    processingStatus: 'not_started',
    fileCount: 0,
    totalBytes: 0,
    totalPages: 0,
    supportedFileTypes: [],
    intendedOutput,
    processingMode: 'multi_file',
    generationJobIds: [],
    documentBriefId: null,
    courseId: null,
    sourceTutorEnabled: false,
    createdBy: input.ownerId,
    createdAt: serverTimestamp() as any,
    updatedAt: serverTimestamp() as any,
    lastProcessedAt: null,
    visibility: input.visibility || 'private',
    progressionMode: input.progressionMode || 'flexible',
    errorMessage: null
  };

  await setDoc(sourceSetRef, sourceSet);

  return sourceSetRef.id;
}

export async function getSourceSet(sourceSetId: string): Promise<SourceSet | null> {
  if (!sourceSetId) {
    throw new Error('sourceSetId is required.');
  }

  const sourceSetRef = doc(db, 'sourceSets', sourceSetId);
  const snapshot = await getDoc(sourceSetRef);

  if (!snapshot.exists()) {
    return null;
  }

  return snapshot.data() as SourceSet;
}

export async function listUserSourceSets(ownerId: string): Promise<SourceSet[]> {
  if (!ownerId) {
    throw new Error('ownerId is required.');
  }

  const sourceSetsQuery = query(
    collection(db, 'sourceSets'),
    where('ownerId', '==', ownerId)
  );

  const snapshot = await getDocs(sourceSetsQuery);

  return snapshot.docs
    .map((sourceSetDoc) => sourceSetDoc.data() as SourceSet)
    .sort((a, b) => {
      const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
      const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
      return bTime - aTime;
    });
}

export async function addSourceFileMetadata(input: CreateSourceFileMetadataInput): Promise<string> {
  if (!input.sourceSetId) {
    throw new Error('sourceSetId is required.');
  }

  if (!input.ownerId) {
    throw new Error('ownerId is required.');
  }

  if (!input.originalFileName) {
    throw new Error('originalFileName is required.');
  }

  if (input.sourceKind !== 'url' && !input.storagePath) {
    throw new Error('storagePath is required for uploaded files.');
  }

  const fileRef = doc(collection(db, 'sourceSets', input.sourceSetId, 'files'));

  const sourceFile: SourceFile = {
    fileId: fileRef.id,
    sourceSetId: input.sourceSetId,
    ownerId: input.ownerId,
    organisationId: input.organisationId || null,
    sourceKind: input.sourceKind || 'file',
    originalFileName: input.originalFileName,
    title: input.title || input.originalFileName,
    url: input.url || '',
    storagePath: input.storagePath || '',
    downloadUrl: input.downloadUrl || '',
    fileType: input.fileType,
    mimeType: input.mimeType || '',
    fileSizeBytes: input.fileSizeBytes,
    pageCount: input.pageCount ?? null,
    textExtractionStatus: 'not_started',
    extractedTextLength: 0,
    chunkCount: 0,
    processingStatus: 'uploaded',
    createdAt: serverTimestamp() as any,
    updatedAt: serverTimestamp() as any,
    errorMessage: null
  };

  await setDoc(fileRef, sourceFile);

  const sourceSetRef = doc(db, 'sourceSets', input.sourceSetId);
  await updateDoc(sourceSetRef, {
    fileCount: increment(1),
    totalBytes: increment(input.fileSizeBytes),
    supportedFileTypes: arrayUnion(input.fileType),
    status: 'uploaded',
    updatedAt: serverTimestamp()
  });

  return fileRef.id;
}

export async function addSourceUrlMetadata(input: CreateSourceUrlMetadataInput): Promise<string> {
  if (!input.sourceSetId) {
    throw new Error('sourceSetId is required.');
  }

  if (!input.ownerId) {
    throw new Error('ownerId is required.');
  }

  const normalisedUrl = normaliseSourceUrl(input.url);
  const existingSources = await listSourceSetFiles(input.sourceSetId);
  const validation = validateSourceSetUrl(normalisedUrl, existingSources);

  if (!validation.valid) {
    throw new Error(validation.errors.join(' '));
  }

  const fileRef = doc(collection(db, 'sourceSets', input.sourceSetId, 'files'));
  const title = String(input.title || getUrlDisplayName(normalisedUrl)).trim();

  const sourceFile: SourceFile = {
    fileId: fileRef.id,
    sourceSetId: input.sourceSetId,
    ownerId: input.ownerId,
    organisationId: input.organisationId || null,
    sourceKind: 'url',
    originalFileName: title,
    title,
    url: normalisedUrl,
    storagePath: '',
    downloadUrl: '',
    fileType: 'url',
    mimeType: 'text/html',
    fileSizeBytes: 0,
    pageCount: null,
    textExtractionStatus: 'not_started',
    extractedTextLength: 0,
    chunkCount: 0,
    processingStatus: 'uploaded',
    createdAt: serverTimestamp() as any,
    updatedAt: serverTimestamp() as any,
    errorMessage: null
  };

  await setDoc(fileRef, sourceFile);

  const sourceSetRef = doc(db, 'sourceSets', input.sourceSetId);
  await updateDoc(sourceSetRef, {
    fileCount: increment(1),
    supportedFileTypes: arrayUnion('url'),
    status: 'uploaded',
    updatedAt: serverTimestamp()
  });

  return fileRef.id;
}

export async function updateSourceSetSettings(
  sourceSetId: string,
  input: UpdateSourceSetSettingsInput
): Promise<void> {
  if (!sourceSetId) {
    throw new Error('sourceSetId is required.');
  }

  const sourceSetRef = doc(db, 'sourceSets', sourceSetId);

  await updateDoc(sourceSetRef, {
    visibility: input.visibility,
    progressionMode: input.progressionMode,
    organisationId: input.visibility === 'organisation' ? (input.organisationId || null) : null,
    updatedAt: serverTimestamp()
  });
}

export async function listSourceSetFiles(sourceSetId: string): Promise<SourceFile[]> {
  if (!sourceSetId) {
    throw new Error('sourceSetId is required.');
  }

  const filesRef = collection(db, 'sourceSets', sourceSetId, 'files');
  const snapshot = await getDocs(filesRef);

  return snapshot.docs
    .map((fileDoc) => fileDoc.data() as SourceFile)
    .sort((a, b) => {
      const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
      const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
      return aTime - bTime;
    });
}

export async function updateSourceSetStatus(
  sourceSetId: string,
  status: SourceSetStatus,
  processingStatus?: string,
  errorMessage?: string | null
): Promise<void> {
  if (!sourceSetId) {
    throw new Error('sourceSetId is required.');
  }

  const sourceSetRef = doc(db, 'sourceSets', sourceSetId);

  await updateDoc(sourceSetRef, {
    status,
    processingStatus: processingStatus || status,
    errorMessage: errorMessage || null,
    updatedAt: serverTimestamp(),
    ...(status === 'ready' ? { lastProcessedAt: serverTimestamp() } : {})
  });
}

export async function attachGenerationJobToSourceSet(
  sourceSetId: string,
  jobId: string
): Promise<void> {
  if (!sourceSetId || !jobId) {
    throw new Error('sourceSetId and jobId are required.');
  }

  const sourceSetRef = doc(db, 'sourceSets', sourceSetId);

  await updateDoc(sourceSetRef, {
    generationJobIds: arrayUnion(jobId),
    updatedAt: serverTimestamp()
  });
}
